import React, { useEffect, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Paper,
  Collapse,
  TablePagination,
  Tooltip,
} from "@mui/material";
import {
  PlayCircle as PlayIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  LiveTv as LiveTvIcon,
} from "@mui/icons-material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { isLocalhost } from "../../utils/isLocalhost";
import { useAuth } from "../../contexts/AuthContext";
import { ExplorerEpisodeProgramEditDialog } from "./ExplorerEpisodeProgramEditDialog";
import { ROUTES_NAMES } from "../../components/routes";
import { useExplorerDates } from "./useExplorerDates";
import { ProgramDetailEpisodeTableRowSkeleton } from "./ExplorerLoadingSkeletons";
import { ExplorerDetailTableHeaderLabel } from "./ExplorerDetailTableHeaderLabel";
import {
  formatNumber,
  formatExplorerTableDateLine,
  formatExplorerTableTimeLine,
  formatExplorerDurationSeconds,
} from "./utils";
import { apiService } from "../../services/api";
import { EpisodeTitleWithHistory } from "./EpisodeTitleWithHistory";
import { ContentDailyMetricsGrowthSection } from "../../components/content-dialog/ContentDailyMetricsGrowthSection";
import type { DailyMetricPoint } from "../ProgramAnalysis/dailyMetricsChart";
// import { detectDailyMetricsAnomalies } from "../ProgramAnalysis/dailyMetricsAnomaly";
// import { EpisodeAnomalyWarning } from "../ProgramAnalysis/AnomalyAlert";

const TABLE_COL_COUNT = 7;
const TABLE_ACTIONS_COL_WIDTH = 204;
const ROWS_PER_PAGE_OPTIONS = [5, 10, 20];

export type ProgramDetailEpisodeItem = {
  _id: string;
  title: string;
  titleHistory?: Array<{ title: string; replacedAt: string }>;
  url?: string;
  youtubeId?: string;
  publishedAt: string;
  duration?: number;
  views: number;
  likes?: number;
  comments?: number;
  livestream?: { channelId: string };
};

function getYouTubeUrl(episode: ProgramDetailEpisodeItem): string | null {
  if (episode.url?.trim()) return episode.url.trim();
  if (episode.youtubeId?.trim()) {
    return `https://www.youtube.com/watch?v=${episode.youtubeId.trim()}`;
  }
  return null;
}

function formatMetricValue(value?: number | null): string {
  const v = Number(value) || 0;
  if (v <= 0) return "—";
  return formatNumber(v);
}

const EpisodeGrowthDetails: React.FC<{ contentId: string }> = ({
  contentId,
}) => {
  const { t } = useTranslation("explorer");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["content-daily-metrics", contentId],
    queryFn: () => apiService.getContentDailyMetrics(contentId),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("metricsLoadError")}
      </Typography>
    );
  }

  if (!data.dailyMetrics?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("noDailyMetricsEpisode")}
      </Typography>
    );
  }

  const dailyMetrics: DailyMetricPoint[] = data.dailyMetrics.map((m) => ({
    collectedAt:
      typeof m.collectedAt === "string"
        ? m.collectedAt
        : m.collectedAt.toISOString(),
    daysSincePublished: m.daysSincePublished,
    publishedAt: data.publishedAt,
    views: m.views,
    likes: m.likes,
    comments: m.comments,
  }));

  return (
    <ContentDailyMetricsGrowthSection
      contentId={contentId}
      dailyMetrics={dailyMetrics}
    />
  );
};

export type ProgramDetailRecentEpisodesTableProps = {
  programId: string;
  podcastId?: string;
  startDate?: string;
  endDate?: string;
  emptyMessage?: string;
};

export const ProgramDetailRecentEpisodesTable: React.FC<
  ProgramDetailRecentEpisodesTableProps
> = ({ programId, podcastId, startDate, endDate, emptyMessage }) => {
  const { t } = useTranslation("explorer");
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { withDates } = useExplorerDates();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedEpisodeId, setCopiedEpisodeId] = useState<string | null>(null);
  const [editEpisode, setEditEpisode] = useState<ProgramDetailEpisodeItem | null>(null);
  const showCopyEpisodeId = isLocalhost();
  const showEditProgram = isAdmin && isLocalhost() && Boolean(podcastId);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]!);

  const handleCopyEpisodeId = (episodeId: string) => {
    navigator.clipboard.writeText(episodeId).then(() => {
      setCopiedEpisodeId(episodeId);
      window.setTimeout(() => setCopiedEpisodeId(null), 2000);
    });
  };

  useEffect(() => {
    setPage(0);
  }, [programId, startDate, endDate]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "explorer-program-episodes",
      programId,
      startDate,
      endDate,
      page,
      rowsPerPage,
    ],
    queryFn: () =>
      apiService.getExplorerProgramEpisodes(
        programId,
        startDate,
        endDate,
        page,
        rowsPerPage,
      ),
    enabled: !!programId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const episodes: ProgramDetailEpisodeItem[] = data?.episodes ?? [];
  const total = data?.total ?? 0;
  const showSkeleton = isLoading && !data;
  const showEmptyRow = !showSkeleton && total === 0;

  // A shorter period can leave the current page past the end.
  useEffect(() => {
    if (!data) return;
    const maxPage = Math.max(0, Math.ceil(data.total / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [data, page, rowsPerPage]);

  /* Anomaly warning prefetch (hidden for now — re-enable with EpisodeAnomalyWarning below)
  const { data: episodesMetricsData } = useQuery({
    queryKey: ["explorer-program-episodes-metrics", programId, startDate, endDate],
    queryFn: () =>
      apiService.getProgramEpisodesDailyMetrics(programId!, {
        startDate,
        endDate,
      }),
    enabled: !!programId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const anomalyByEpisodeId = React.useMemo(() => {
    const map = new Map<string, ReturnType<typeof detectDailyMetricsAnomalies>>();
    if (!episodesMetricsData?.episodes) return map;
    for (const ep of episodesMetricsData.episodes) {
      map.set(ep._id, detectDailyMetricsAnomalies(ep.dailyMetrics));
    }
    return map;
  }, [episodesMetricsData]);
  */

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        {showSkeleton ? (
          <Skeleton variant="text" width={240} height={36} />
        ) : (
          <Typography variant="h2" color="text.primary">
            Videos recientes ({total})
          </Typography>
        )}
        {isFetching && !showSkeleton && <CircularProgress size={16} />}
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: 108, whiteSpace: "nowrap" }}>
                <ExplorerDetailTableHeaderLabel
                  label={t("date")}
                  tooltip={t("tooltip.episodePublishedAt")}
                />
              </TableCell>
              <TableCell align="left" sx={{ minWidth: 320 }}>
                <ExplorerDetailTableHeaderLabel
                  label={t("title")}
                  tooltip={t("tooltip.episodeName")}
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("duration")}
                  tooltip={t("tooltip.episodeDuration")}
                  align="right"
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("views")}
                  tooltip={t("tooltip.episodeViews")}
                  align="right"
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("likes")}
                  tooltip={t("tooltip.episodeLikes")}
                  align="right"
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("comments")}
                  tooltip={t("tooltip.episodeComments")}
                  align="right"
                />
              </TableCell>
              <TableCell
                align="right"
                sx={{ width: TABLE_ACTIONS_COL_WIDTH, px: 0.5 }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton ? (
              Array.from({ length: rowsPerPage }, (_, i) => (
                <ProgramDetailEpisodeTableRowSkeleton
                  key={`episode-table-skeleton-${i}`}
                />
              ))
            ) : showEmptyRow ? (
              <TableRow>
                <TableCell
                  colSpan={TABLE_COL_COUNT}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage ?? t("noEpisodes")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              episodes.map((episode) => {
                const youtubeUrl = getYouTubeUrl(episode);
                const isExpanded = expandedId === episode._id;
                // const anomaly = anomalyByEpisodeId.get(episode._id);

                return (
                  <React.Fragment key={episode._id}>
                    <TableRow
                      hover
                      onClick={(e) => {
                        e.preventDefault();
                        setExpandedId((prev) =>
                          prev === episode._id ? null : episode._id,
                        );
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell
                        align="left"
                        sx={{ width: 108, whiteSpace: "nowrap" }}
                      >
                        <Stack spacing={0.25}>
                          <Typography variant="body2" color="text.primary">
                            {formatExplorerTableDateLine(episode.publishedAt)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatExplorerTableTimeLine(episode.publishedAt)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ minWidth: 320, whiteSpace: "normal" }}
                      >
                        <EpisodeTitleWithHistory
                          title={episode.title}
                          titleHistory={episode.titleHistory}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatExplorerDurationSeconds(episode.duration)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatMetricValue(episode.views)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatMetricValue(episode.likes)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatMetricValue(episode.comments)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ width: TABLE_ACTIONS_COL_WIDTH, px: 0.5 }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          spacing={0}
                          sx={{ flexShrink: 0 }}
                        >
                          {showEditProgram ? (
                            <Tooltip title={t("editAssignedProgram")}>
                              <IconButton
                                size="small"
                                color="secondary"
                                aria-label={t("editProgram")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditEpisode(episode);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {showCopyEpisodeId ? (
                            <Tooltip
                              title={
                                copiedEpisodeId === episode._id
                                  ? t("copied")
                                  : t("copyEpisodeId")
                              }
                            >
                              <IconButton
                                size="small"
                                aria-label={t("copyEpisodeId")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyEpisodeId(episode._id);
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {episode.livestream && episode.youtubeId ? (
                            <Tooltip title={t("openLivestreamEmission")}>
                              <IconButton
                                size="small"
                                sx={{ color: "podscope.main" }}
                                aria-label={t("openLivestreamEmission")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const path = generatePath(
                                    ROUTES_NAMES.EXPLORER_LIVESTREAM_CHANNEL,
                                    { channelId: episode.livestream!.channelId },
                                  );
                                  navigate(
                                    withDates(
                                      `${path}?videoId=${encodeURIComponent(episode.youtubeId!)}`,
                                    ),
                                  );
                                }}
                              >
                                <LiveTvIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {youtubeUrl ? (
                            <IconButton
                              size="small"
                              sx={{ color: "error.main" }}
                              aria-label={t("openOnYouTube")}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  youtubeUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }}
                            >
                              <PlayIcon />
                            </IconButton>
                          ) : null}
                          <IconButton
                            size="small"
                            aria-label={
                              isExpanded ? t("collapseRow") : t("expandRow")
                            }
                            tabIndex={-1}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={TABLE_COL_COUNT}
                        sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}
                      >
                        <Collapse
                          in={isExpanded}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 1 }}>
                            <EpisodeGrowthDetails contentId={episode._id} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {!showSkeleton && total > 0 ? (
          <TablePagination
            component="div"
            count={total}
            page={Math.min(page, Math.max(0, Math.ceil(total / rowsPerPage) - 1))}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage={t("rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              t("pagination.range", {
                from,
                to,
                shown: count !== -1 ? count : t("pagination.moreThan", { to }),
              })
            }
          />
        ) : null}
      </TableContainer>
      {showEditProgram && editEpisode && podcastId ? (
        <ExplorerEpisodeProgramEditDialog
          open
          onClose={() => setEditEpisode(null)}
          podcastId={podcastId}
          contentId={editEpisode._id}
          contentTitle={editEpisode.title}
          currentProgramId={programId}
        />
      ) : null}
    </Box>
  );
};
